import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { Link as RouterLink } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { useSnackbar } from 'notistack';
import shopifyService from '../../services/shopifyService';
import API_CONFIG from '../../config';

const statusColor = (status) => {
  switch (status) {
    case 'CONVERTED':
      return 'success';
    case 'FAILED':
      return 'error';
    case 'PROCESSING':
      return 'warning';
    default:
      return 'default';
  }
};

const ShopifyOrdersPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(0);

  const loadOrders = async (p = page) => {
    try {
      setLoading(true);
      const data = await shopifyService.listOrders(p, 25);
      setOrders(data.content || []);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || 'Failed to load orders', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders(page);
  }, [page]);

  const handleRetry = async (id) => {
    try {
      await shopifyService.retryOrder(id);
      enqueueSnackbar('Retry completed', { variant: 'success' });
      loadOrders(page);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || 'Retry failed', { variant: 'error' });
    }
  };

  const receiptUrl = (salesOrderId) =>
    `${API_CONFIG.BASE_URL}/sales-orders/${salesOrderId}/receipt`;

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">Shopify Orders</Typography>
          <Box>
            <Button component={RouterLink} to="/settings/shopify" sx={{ mr: 1 }}>
              Settings
            </Button>
            <IconButton onClick={() => loadOrders(page)} aria-label="refresh">
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Shopify #</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Tiya bill</TableCell>
                  <TableCell>Payment</TableCell>
                  <TableCell>Error</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No Shopify orders yet
                    </TableCell>
                  </TableRow>
                ) : (
                  orders.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.shopifyOrderNumber || row.shopifyOrderId}</TableCell>
                      <TableCell>
                        <Chip size="small" label={row.orderStatus} color={statusColor(row.orderStatus)} />
                      </TableCell>
                      <TableCell>
                        {row.tiyaOrderNumber || (row.tiyaSalesOrderId ? `#${row.tiyaSalesOrderId}` : '—')}
                      </TableCell>
                      <TableCell>{row.paymentStatus}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="error">
                          {row.syncError || ''}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {row.orderStatus === 'FAILED' && (
                          <Tooltip title="Retry conversion">
                            <IconButton size="small" onClick={() => handleRetry(row.id)}>
                              <ReplayIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {row.tiyaSalesOrderId && (
                          <Tooltip title="Download receipt PDF">
                            <IconButton
                              size="small"
                              component="a"
                              href={receiptUrl(row.tiyaSalesOrderId)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ReceiptIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </Layout>
  );
};

export default ShopifyOrdersPage;
